/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import { Card, Typography, Chip, Box, Grid, IconButton, Tooltip, Stack, Button } from '@mui/material';
import {
	Person as PersonIcon,
	FlightTakeoff as FlightIcon,
	Business as BusinessIcon,
	Payment as PaymentIcon,
	Settings as SettingsIcon,
	History as HistoryIcon,
	OpenInNew as OpenInNewIcon,
	AccessTime as AccessTimeIcon,
} from '@mui/icons-material';
import { IFlightAndFlightAncillaryIssueSummary } from '../../types';
import { transformValue } from '../FlightAndAncillaryIssueSummaryList/constants';
import routes from 'constants/routes';
import DealOrCaseOpenDetail from '../../../common/components/DealOrCaseOpenDetail';
import { findCountryWithAgencyConfigForUser } from '../../../auth/helpers';

interface FlightIssuingControlCardProps {
	item: IFlightAndFlightAncillaryIssueSummary;
}

interface CountdownChipProps {
	ticketingLimitDatetime: string;
}

const CountdownChip: React.FC<CountdownChipProps> = ({ ticketingLimitDatetime }) => {
	const [timeLeft, setTimeLeft] = useState<{
		hours: number;
		minutes: number;
		isExpired: boolean;
	}>({ hours: 0, minutes: 0, isExpired: false });

	useEffect(() => {
		const calculateTimeLeft = () => {
			// Fecha actual
			const now = new Date();

			// Parsear la fecha límite
			const limitDate = new Date(ticketingLimitDatetime);

			// Calcular diferencia en milisegundos
			const diff = limitDate.getTime() - now.getTime();

			if (diff <= 0) {
				return { hours: 0, minutes: 0, isExpired: true };
			}

			const hours = Math.floor(diff / (1000 * 60 * 60));
			const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

			return { hours, minutes, isExpired: false };
		};

		// Calcular inicial
		setTimeLeft(calculateTimeLeft());

		// Actualizar cada minuto
		const interval = setInterval(() => {
			setTimeLeft(calculateTimeLeft());
		}, 60000);

		return () => clearInterval(interval);
	}, [ticketingLimitDatetime]);

	const getColorStyles = () => {
		if (timeLeft.isExpired) {
			return {
				backgroundColor: '#d32f2f',
				color: 'white',
			};
		}

		const totalHours = timeLeft.hours + timeLeft.minutes / 60;

		if (totalHours > 6) {
			return {
				backgroundColor: '#2e7d32',
				color: 'white',
			};
		}
		if (totalHours > 3) {
			return {
				backgroundColor: '#ed6c02',
				color: 'white',
			};
		}
		return {
			backgroundColor: '#d32f2f',
			color: 'white',
		};
	};

	const getDisplayText = () => {
		if (timeLeft.isExpired) return 'Vencido';

		const hoursText = timeLeft.hours > 0 ? `${timeLeft.hours}h` : '';
		const minutesText = `${timeLeft.minutes.toString().padStart(2, '0')}m`;

		return timeLeft.hours > 0 ? `${hoursText} ${minutesText}` : minutesText;
	};

	return (
		<Chip
			icon={<AccessTimeIcon sx={{ color: 'white !important' }} />}
			label={getDisplayText()}
			size="small"
			sx={{
				fontWeight: 'bold',
				...getColorStyles(),
				...(timeLeft.isExpired && {
					animation: 'blink 1s infinite',
				}),
			}}
		/>
	);
};

export const FlightIssuingControlCard: React.FC<FlightIssuingControlCardProps> = ({ item }) => {
	const toTitleCase = (str: string) => {
		return str
			.toLowerCase()
			.split(' ')
			.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
			.join(' ');
	};

	const getStatusColor = (status: string) => {
		switch (status?.toLowerCase()) {
			case 'issued':
			case 'emitido':
				return 'success';
			case 'pending':
			case 'pendiente':
				return 'info';
			default:
				return 'default';
		}
	};

	const getAgencyName = (agencyId: number) => {
		const agencyConfig = findCountryWithAgencyConfigForUser(agencyId);
		return agencyConfig ? agencyConfig.countryName : `Agencia ${agencyId}`;
	};

	const handleOpenSaleOrderDetail = () => {
		const url = routes.salesOrder.edit.replace(':id', item.saleOrderId);
		window.open(`${url}?serviceId=${item.serviceId}`, '_blank');
	};

	// Verificar si debe mostrar el countdown
	const shouldShowCountdown = () => {
		const isPending =
			item.ticketingStatus?.toLowerCase() === 'pending' || item.ticketingStatus?.toLowerCase() === 'pendiente';
		return isPending && item.ticketingRequested && item.ticketingLimitDatetime;
	};

	return (
		<Card sx={{ width: '100%', mb: 2, borderRadius: 2, border: '1px solid #e0e0e0' }}>
			{/* Header Row */}
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					p: 2,
					borderBottom: '1px solid #f0f0f0',
					backgroundColor: '#fafafa',
				}}
			>
				<Stack direction="row" alignItems="center" spacing={2}>
					<PersonIcon color="primary" />
					<Typography variant="h6" component="h3">
						{toTitleCase(item.mainPassengerCompleteName)}
					</Typography>
					<Typography variant="body2" color="text.secondary">
						<strong>PNR:</strong> {item.serviceReference}
					</Typography>
					<Chip
						label={transformValue('ticketingStatus', item.ticketingStatus, item)}
						color={getStatusColor(item.ticketingStatus) as any}
						size="small"
					/>
					{!item.ticketingRequested && (
						<Chip label="Emisión NO Solicitada" size="small" variant="outlined" color="error" />
					)}
					{!item.serviceEnable && <Chip label="Deshabilitado" size="small" variant="outlined" color="error" />}
				</Stack>

				<Stack direction="row" alignItems="center" spacing={1}>
					{shouldShowCountdown() && <CountdownChip ticketingLimitDatetime={item.ticketingLimitDatetime} />}
					<Tooltip title="Abrir Sale Order">
						<IconButton size="small" onClick={handleOpenSaleOrderDetail}>
							<OpenInNewIcon fontSize="small" />
						</IconButton>
					</Tooltip>
				</Stack>
			</Box>

			{/* Content Row */}
			<Box sx={{ p: 2 }}>
				<Grid container spacing={2}>
					{/* Column 1: Información del servicio */}
					<Grid item xs={12} md={3}>
						<Typography
							variant="subtitle2"
							color="primary"
							sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}
						>
							<FlightIcon fontSize="small" />
							Información del servicio
						</Typography>
						<Stack spacing={0.5}>
							<Typography variant="body2">
								<strong>ID:</strong> {item.serviceId}
							</Typography>
							<Typography variant="body2" sx={{ mb: 1 }}>
								<strong>Límite:</strong>
							</Typography>
							<Button
								variant="outlined"
								size="small"
								disabled
								sx={{
									color: 'text.primary',
									borderColor: 'grey.400',
									cursor: 'default',
									'&.Mui-disabled': {
										color: 'text.primary',
										borderColor: 'grey.400',
									},
								}}
							>
								{transformValue('ticketingLimitDatetime', item.ticketingLimitDatetime, item)}
							</Button>
							<Typography variant="body2">
								<strong>Tipo:</strong> {transformValue('ticketType', item.ticketType, item)}
							</Typography>
							<Typography variant="body2">
								<strong>GDS:</strong> {transformValue('gds', item.gds, item)} | <strong>VC:</strong>{' '}
								{transformValue('vcCode', item.vcCode, item)}
							</Typography>
							<Typography variant="body2">
								<strong>OID:</strong> {item.oidName || '-'}
							</Typography>
							<Typography variant="body2">
								<strong>Costo:</strong> {transformValue('serviceCost', item.serviceCost, item)}
							</Typography>
						</Stack>
					</Grid>

					{/* Column 2: Orden de venta */}
					<Grid item xs={12} md={3}>
						<Typography
							variant="subtitle2"
							color="primary"
							sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}
						>
							<BusinessIcon fontSize="small" />
							Orden de venta
						</Typography>
						<Stack spacing={0.5}>
							<Typography variant="body2">
								<strong>ID:</strong> {item.saleOrderId}
							</Typography>
							<Typography variant="body2">
								<strong>Monto:</strong> {transformValue('saleOrderChargedAmount', item.saleOrderChargedAmount, item)}
							</Typography>
							<Typography variant="body2">
								<strong>Segmento:</strong> {item.saleOrderSegmentType}
							</Typography>
							<Typography variant="body2">
								<strong>Salida:</strong>{' '}
								{item.saleOrderTravelDepartureDate
									? new Date(item.saleOrderTravelDepartureDate).toLocaleDateString()
									: '-'}
							</Typography>
							<Typography variant="body2">
								<strong>Caso de venta:</strong>{' '}
								<DealOrCaseOpenDetail dealId={item.saleOrderDealId} isBitrixDeal={item.saleOrderIsBitrixDeal} />
							</Typography>
							<Typography variant="body2">
								<strong>Creado por:</strong> {item.saleOrderCreatedBy}
							</Typography>
							<Typography variant="body2">
								<strong>Agencia:</strong> {getAgencyName(item.saleOrderAgencyId)}
							</Typography>
						</Stack>
					</Grid>

					{/* Column 3: Proveedor y Workflow */}
					<Grid item xs={12} md={3}>
						<Typography
							variant="subtitle2"
							color="primary"
							sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}
						>
							<SettingsIcon fontSize="small" />
							Workflow de Emisión
						</Typography>
						<Stack spacing={0.5}>
							{item.issueRequestCaseId && (
								<>
									<Typography
										variant="body2"
										component="a"
										href={`https://crm.mevuelo.com/dashboard/cases/${item.issueRequestCaseId}`}
										target="_blank"
										rel="noopener noreferrer"
										sx={{
											color: 'primary.main',
											textDecoration: 'none',
											cursor: 'pointer',
											'&:hover': {
												textDecoration: 'none',
											},
										}}
									>
										<strong>Caso de emisión:</strong> {item.issueRequestCaseId}
									</Typography>
									<Typography variant="body2">
										<strong>Caso creado por:</strong> {item.issueRequestCaseCreatedBy}
									</Typography>
									<Typography variant="body2">
										<strong>Fecha caso:</strong>{' '}
										{transformValue('issueRequestCaseCreatedAt', item.issueRequestCaseCreatedAt, item)}
									</Typography>
									<Typography variant="body2">
										<strong>CRM Agencia:</strong> {transformValue('crmAgencyName', item.crmAgencyName, item)}
									</Typography>
									<Typography variant="body2">
										<strong>Workflow:</strong> {item.crmWorkflowName || '-'}
									</Typography>
									<Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
										<strong>Etapa:</strong>
										{item.crmSteepName ? (
											item.crmSteepName === 'Done' ? (
												<Chip label={item.crmSteepName} size="small" color="success" />
											) : item.crmSteepName === 'Fail' ? (
												<Chip label={item.crmSteepName} size="small" color="error" />
											) : (
												item.crmSteepName
											)
										) : (
											'-'
										)}
									</Typography>
									<Typography variant="body2">
										<strong>Agente:</strong> {transformValue('crmAgentCompleteName', item.crmAgentCompleteName, item)}
									</Typography>
								</>
							)}
						</Stack>
					</Grid>

					{/* Column 4: Auditoría */}
					<Grid item xs={12} md={3}>
						<Typography
							variant="subtitle2"
							color="primary"
							sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}
						>
							<HistoryIcon fontSize="small" />
							Auditoría
						</Typography>
						<Stack spacing={0.5}>
							<Typography variant="body2">
								<strong>Creado:</strong> {transformValue('serviceCreatedAt', item.serviceCreatedAt, item)}
							</Typography>
							<Typography variant="body2">
								<strong>Proveedor:</strong> {item.providerFantasyName || '-'}
							</Typography>
							{/* {item.providerBusinessName && (
								<Typography variant="body2">
									<strong>Razón social:</strong>{' '}
									{transformValue('providerBusinessName', item.providerBusinessName, item)}
								</Typography>
							)} */}
							<Typography variant="body2">
								<strong>Actualizado:</strong> {transformValue('serviceUpdatedAt', item.serviceUpdatedAt, item)}
							</Typography>
							<Typography variant="body2">
								<strong>Por:</strong> {transformValue('serviceUpdatedBy', item.serviceUpdatedBy, item)}
							</Typography>
						</Stack>
					</Grid>
				</Grid>

				{/* Payments Section (if exists) */}
				{item.cardToAirlinePaymentLinkSummaryLines && item.cardToAirlinePaymentLinkSummaryLines.length > 0 && (
					<Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #f0f0f0' }}>
						<Typography
							variant="subtitle2"
							color="primary"
							sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}
						>
							<PaymentIcon fontSize="small" />
							Pagos a aerolínea ({item.cardToAirlinePaymentLinkSummaryLines.length})
						</Typography>
						<Grid container spacing={1}>
							{item.cardToAirlinePaymentLinkSummaryLines?.map((line, i) => (
								<Grid
									item
									xs={12}
									md={4}
									lg={2}
									key={`${item.serviceId}-${line.cardToAirlineOrderId || i}`}
									sx={{ display: 'flex' }}
								>
									<Box
										sx={{
											p: 1,
											bgcolor: 'grey.50',
											borderRadius: 1,
											border: '1px solid #e0e0e0',
											width: '100%',
											display: 'flex',
											flexDirection: 'column',
											justifyContent: 'space-between',
										}}
									>
										<Chip
											label={
												line.status === 'sent'
													? 'Link enviado a cliente'
													: line.status === 'completed'
													? 'Listo para cobrar'
													: line.status
											}
											size="small"
											color={line.status === 'completed' ? 'success' : 'warning'}
										/>
										<br />
										<br />
										<Typography variant="body2">
											<strong>Monto:</strong> ${line.amount.toLocaleString()}
										</Typography>
										<Typography variant="body2">
											<strong>Cuotas:</strong> {line.fee && `${line.fee}`}
										</Typography>
										<Typography variant="body2">
											<strong>Card:</strong> {line.cardStamp || '-'}
										</Typography>
										{line.transactionId && (
											<Typography
												variant="body2"
												component="a"
												href={`/transaction/${line.transactionId}`}
												target="_blank"
												rel="noopener noreferrer"
												sx={{
													color: 'primary.main',
													textDecoration: 'none',
													cursor: 'pointer',
													'&:hover': {
														textDecoration: 'none',
													},
												}}
											>
												<strong>Transaction ID:</strong> {line.transactionId}
											</Typography>
										)}
										<br />
										{line.caseIdCompleteLink && (
											<Typography
												variant="body2"
												component="a"
												href={`https://crm.mevuelo.com/dashboard/cases/${line.caseIdCompleteLink}`}
												target="_blank"
												rel="noopener noreferrer"
												sx={{
													color: 'primary.main',
													textDecoration: 'none',
													cursor: 'pointer',
													'&:hover': {
														textDecoration: 'none',
													},
												}}
											>
												<strong>Case Link ID:</strong> {line.caseIdCompleteLink}
											</Typography>
										)}
										<Typography variant="body2">
											<strong>Creado:</strong> {new Date(line.createdAt).toLocaleDateString()}
										</Typography>
									</Box>
								</Grid>
							))}
						</Grid>
					</Box>
				)}
			</Box>
		</Card>
	);
};
